import { useEffect, useState } from 'react';
import styles from './productAdd.module.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { popUpBoxTrigger } from "../../features/message/popUpSlice";
import PopUpMessage from "../../utils/errors/PopUpMessage";
import { createProduct, warehouseList, allCompanyList, uploadImages } from '../../services/Api';
import { resetProductTransaction } from '../../features/scanproduct/scanProduct';

// Custom Hooks
import { useProductForm } from '../../hooks/useProductForm';
import { useAttributes } from '../../hooks/useAttributes';

// Components
import ProductBasicInfo from './components/ProductBasicInfo/ProductBasicInfo';
import CategorySelector from './components/CategorySelector/CategorySelector';
import BrandSelector from './components/BrandSelector/BrandSelector';
import AttributeFields from './components/AttributeFields/AttributeFields';
import MultipleSKU from './components/MultipleSKU/MultipleSKU';
// import OptionalAttributes from './components/OptionalAttributes/OptionalAttributes';
import ImageUploader from './components/ImageUploader/ImageUploader';
import PerishableFields from './components/PerishableFields/PerishableFields';
import BarcodeField from './components/BarcodeField/BarcodeField';
import CustomCKEditor from '../../components/common/CKEditor/CKEditor';
import FormButtons from './components/FormButtons/FormButtons';
import { useBrands } from '../../hooks/useBrands';

const ProductAdd = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Form state management - errors is now provided by useProductForm
  const {
    formData,
    setFormData,
    updateFormData,
    errors, // errors comes from useProductForm
    setErrors,
    clearError,
    isBarcodeFromScan,
    setIsBarcodeFromScan,
    enableManualBarcodeEdit,
    transactionId
  } = useProductForm();

  // Local state - REMOVE errors from here
  const [loading, setLoading] = useState(false);
  // const [errors, setErrors] = useState({}); // REMOVE THIS LINE - it's duplicate
  const [previewImages, setPreviewImages] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Category state
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [selectedCategoryName, setSelectedCategoryName] = useState('');

  // Brand state
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // SKU and Package state
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState('');
  const [packageData, setPackageData] = useState({
    height: '',
    length: '',
    width: '',
    weight: ''
  });

  const [skus, setSkus] = useState([
    {
      SellerSku: `SKU${Date.now()}`,
      quantity: "1",
      price: "",
      package_height: "",
      package_length: "",
      package_width: "",
      package_weight: "",
      Images: {
        Image: []
      },
      saleProp: {},
      package_content: "Single Item",
      tax_class: "default",
      Status: "active"
    }
  ]);

  // Attributes management
  const {
    attributes,
    attributeValues,
    // optionalAttributes,
    mandatoryAttributes,
    skuAttributes,
    setAttributeValues,
    loadingAttributes,
    fieldsToHide,
    shouldHideField,
    fetchAttributes
  } = useAttributes();

  // Filter out propCascade from mandatory attributes
  const filteredMandatoryAttributes = mandatoryAttributes.filter(
    attr => attr.name !== "propCascade"
  );

  // Initialize data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [warehousesResponse, companiesResponse] = await Promise.all([
          warehouseList(),
          allCompanyList({})
        ]);
        setWarehouses(warehousesResponse.data);
        setCompanies(companiesResponse.data);
      } catch (error) {
        showPopup('error', 'Error', 'Failed to load initial data');
      }
    };

    fetchInitialData();

    if (!transactionId) {
      dispatch(resetProductTransaction());
    }
  }, [transactionId, dispatch]);

  const {
    brandSuggestions,
    loading: brandsLoading,
    searchBrands,
    clearSearch,
    hasFetched
  } = useBrands();

  // Brand search handler
  const handleBrandSearch = (searchTerm) => {
    searchBrands(searchTerm);
  };

  // Brand selection handler
  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);

    // Handle "No Brand" selection
    if (brand.name === 'No Brand') {
      setBrandSearchTerm('');
      updateFormData('brand_name', '');
      updateFormData('brand_id', '');
    } else {
      updateFormData('brand_id', brand.brand_id ? brand.brand_id.toString() : '');
      setBrandSearchTerm(brand.name);
      updateFormData('brand_name', brand.name);
    }

    setShowBrandDropdown(false);

    if (errors.brand_name) {
      setErrors(prev => ({ ...prev, brand_name: '' }));
    }

    // Update attributeValues for brand
    setAttributeValues(prev => ({
      ...prev,
      brand: brand.name === 'No Brand' ? '' : brand.name
    }));
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    console.log('=== VALIDATING FORM ===');

    // Get warehouse_id from localStorage
    const userWarehouseId = localStorage.getItem('warehouse_id');
    console.log('User warehouse_id from localStorage:', userWarehouseId);

    // Company
    if (!formData.company_id) {
      newErrors.company_id = 'Company is required';
    }

    // Category
    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    // Brand Name - UPDATED: Check if brand is required based on attributes
    const brandAttribute = attributes.find(attr => attr.name === 'brand');
    const isBrandMandatory = brandAttribute && brandAttribute.is_mandatory === 1;

    let brandName = '';
    let brandFound = false;

    // Priority 1: Check selectedBrand
    if (selectedBrand && selectedBrand.name && selectedBrand.name.trim() !== '') {
      brandName = selectedBrand.name;
      brandFound = true;
    }

    // Priority 2: Check formData.brand_name
    if (!brandFound && formData.brand_name && formData.brand_name.trim() !== '') {
      if (typeof formData.brand_name === 'object' && formData.brand_name.name) {
        brandName = formData.brand_name.name;
      } else {
        brandName = formData.brand_name;
      }
      brandFound = true;
    }

    // Priority 3: Check attributeValues.brand
    if (!brandFound && attributeValues.brand) {
      if (typeof attributeValues.brand === 'object' && attributeValues.brand.name) {
        brandName = attributeValues.brand.name;
      } else if (typeof attributeValues.brand === 'string' && attributeValues.brand.trim() !== '') {
        brandName = attributeValues.brand;
      }
      brandFound = true;
    }

    // Final brand validation - ONLY if brand is mandatory
    if (isBrandMandatory && (!brandFound || !brandName || brandName.trim() === '')) {
      newErrors.brand_name = 'Brand name is required';
    }

    // Warehouse - ONLY validate if user doesn't have a warehouse_id in localStorage
    // Check both localStorage and formData
    const hasValidUserWarehouseId = userWarehouseId &&
      userWarehouseId.trim() !== '' &&
      userWarehouseId !== 'null' &&
      userWarehouseId !== 'undefined';

    // If user doesn't have a warehouse_id, check formData
    if (!hasValidUserWarehouseId && (!formData.warehouse_id || formData.warehouse_id.toString().trim() === '')) {
      newErrors.warehouse_id = 'Warehouse is required';
    }

    // Product Name
    const productNameFromForm = formData.product_name;
    const productNameFromAttributes = attributeValues.name;
    const productName = productNameFromForm || productNameFromAttributes;

    if (!productName || productName.trim() === '') {
      newErrors.product_name = 'Product name is required';
    }

    // Rest of your validation code remains the same...
    // Description
    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Description is required';
    }

    // Warehouse
    if (!formData.warehouse_id) {
      newErrors.warehouse_id = 'Warehouse is required';
    }

    // Barcode
    if (!formData.barcode_number || formData.barcode_number.trim() === '') {
      newErrors.barcode_number = 'Barcode is required';
    }

    // In validateForm function - Update the attribute validation section
    // Define SKU attribute names and sale properties to exclude from validation
    const skuAttributeNames = ['SellerSku', 'sku', 'price', 'package_weight', 'package_length', 'package_width', 'package_height', 'quantity'];
    const saleProperties = ['description', 'color_family', 'size']; // Add fields that should only be in SKU

    attributes.forEach(attr => {
      // Skip validation for SKU-related attributes AND sale properties
      if (skuAttributeNames.includes(attr.name) || saleProperties.includes(attr.name) || attr.name === "propCascade") {
        return;
      }

      // Skip brand attribute validation as we already handled it above
      if (attr.name === 'brand') {
        return;
      }

      if (attr.is_mandatory === 1) {
        const value = attributeValues[attr.name];
        const isEmpty = value === null || value === undefined || value === '' || value === '0' || value === 'undefined';

        if (isEmpty) {
          newErrors[`attribute_${attr.name}`] = `${attr.label} is required`;
        }
      }
    });

    // Validate SKUs - This is where SKU attributes should be validated
    skus.forEach((sku, index) => {
      if (!sku.SellerSku || sku.SellerSku.trim() === '') {
        newErrors[`skus.${index}.SellerSku`] = 'Seller SKU is required';
      }

      if (!sku.quantity || sku.quantity.trim() === '' || parseInt(sku.quantity) <= 0) {
        newErrors[`skus.${index}.quantity`] = 'Valid quantity is required';
      }

      if (!sku.price || sku.price.trim() === '' || parseFloat(sku.price) <= 0) {
        newErrors[`skus.${index}.price`] = 'Valid price is required';
      }

      if (!sku.package_weight || sku.package_weight.trim() === '') {
        newErrors[`skus.${index}.package_weight`] = 'Package weight is required';
      }

      if (!sku.package_length || sku.package_length.trim() === '') {
        newErrors[`skus.${index}.package_length`] = 'Package length is required';
      }

      if (!sku.package_width || sku.package_width.trim() === '') {
        newErrors[`skus.${index}.package_width`] = 'Package width is required';
      }

      if (!sku.package_height || sku.package_height.trim() === '') {
        newErrors[`skus.${index}.package_height`] = 'Package height is required';
      }
    });

    // Perishable product validation
    if (formData.productType === 'perishable') {
      if (!formData.storage_type) {
        newErrors.storage_type = 'Storage type is required';
      }
      if (!formData.expire_date) {
        newErrors.expire_date = 'Expiry date is required';
      } else {
        const expireDate = new Date(formData.expire_date);
        if (expireDate <= today) {
          newErrors.expire_date = 'Expiry date must be after today';
        }
      }
    }

    console.log('Total errors found:', Object.keys(newErrors).length);
    console.log('Validation errors:', newErrors);
    console.log('Brand validation result:', {
      brandFound,
      brandName,
      formDataBrand: formData.brand_name,
      attributeBrand: attributeValues.brand,
      selectedBrand,
      isBrandMandatory
    });
    return newErrors;
  };

  const showPopup = (type, heading, message) => {
    dispatch(popUpBoxTrigger({ type, heading, message, show: true }));
  };

  const handleCategorySelect = async (category) => {
    setSelectedCategoryName(category.categoryName);
    updateFormData('category_id', category.categoryId);
    setCategorySearchTerm(category.categoryName);

    if (errors.category_id) {
      setErrors(prev => ({ ...prev, category_id: '' }));
    }

    await fetchAttributes(category.categoryId);
  };

  const handleAttributeChange = (attributeName, value) => {
    const attribute = attributes.find(attr => attr.name === attributeName);

    if (attribute && (attribute.input_type === 'multiSelect' || attribute.input_type === 'multiEnumInput')) {
      // For multi-select, value should be an array of selected values
      // Ensure we're storing the array properly
      setAttributeValues(prev => ({
        ...prev,
        [attributeName]: Array.isArray(value) ? value : (value ? [value] : [])
      }));
    } else {
      // Normal attribute handling
      setAttributeValues(prev => ({
        ...prev,
        [attributeName]: value
      }));
    }

    // Update product name if the name attribute changes
    if (attributeName === 'name') {
      updateFormData('product_name', value);
    }

    // Sync brand to formData when brand attribute changes
    if (attributeName === 'brand') {
      // FIX: If value is an object, extract name and brand_id
      if (value && typeof value === 'object' && value.name) {
        updateFormData('brand_name', value.name);
        if (value.brand_id) {
          updateFormData('brand_id', value.brand_id.toString());
        }
      } else {
        updateFormData('brand_name', value);
      }

      // If we have a selected brand with ID, update brand_id as well
      if (selectedBrand && selectedBrand.brand_id) {
        updateFormData('brand_id', selectedBrand.brand_id.toString());
      }
    }

    const errorKey = `attribute_${attributeName}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const clearAttributeError = (attributeName) => {
    const errorKey = `attribute_${attributeName}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const maxSizeInBytes = 2048 * 1024;

    if (formData.product_images.length + files.length > 5) {
      showPopup('error', 'Error', 'Maximum 5 product images allowed');
      return;
    }

    for (const file of files) {
      if (file.size > maxSizeInBytes) {
        showPopup('error', 'Error', `${file.name} exceeds the maximum size of 2 MB`);
        await new Promise(r => setTimeout(r, 0)); // Let popup render
        continue;
      }

      const isDuplicate = formData.product_images.some(img => img.name === file.name && img.size === file.size);
      if (isDuplicate) {
        showPopup('error', 'Duplicate Image', `Same image cannot be uploaded again: ${file.name}`);
        await new Promise(r => setTimeout(r, 0));
        continue;
      }

      const imageUrl = URL.createObjectURL(file);
      const image = new Image();
      image.src = imageUrl;

      await new Promise((resolve) => {
        image.onload = async () => {
          if (image.width < 330 || image.height < 330) {
            showPopup('error', 'Invalid Dimensions', `${file.name} must be at least 330x330 pixels`);
            URL.revokeObjectURL(imageUrl);
            await new Promise(r => setTimeout(r, 0));
            return resolve();
          }

          // Valid image
          setFormData(prev => ({
            ...prev,
            product_images: [...prev.product_images, { url: imageUrl, name: file.name, size: file.size, file }]
          }));
          setPreviewImages(prev => [...prev, imageUrl]);
          setErrors(prev => ({ ...prev, product_images: '' }));
          return resolve();
        };

        image.onerror = async () => {
          showPopup('error', 'Invalid File', `Failed to load image: ${file.name}`);
          URL.revokeObjectURL(imageUrl);
          await new Promise(r => setTimeout(r, 0));
          return resolve();
        };
      });
    }

    e.target.value = "";
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      product_images: prev.product_images.filter((_, i) => i !== index)
    }));
    URL.revokeObjectURL(previewImages[index]);
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
  };

  const generateSKU = () => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const year = now.getFullYear().toString().slice(2);
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hour = pad(now.getHours());
    const minute = pad(now.getMinutes());
    const second = pad(now.getSeconds());
    const generatedSku = `${year}${month}${day}${hour}${minute}${second}`;
    setSku(generatedSku);
  };

  const handleCancel = () => {
    navigate(-1); // or navigate('/products')
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear any existing SKU attribute errors before validation
    const skuAttributeNames = ['SellerSku', 'sku', 'price', 'package_weight', 'package_length', 'package_width', 'package_height', 'quantity'];
    const cleanedErrors = { ...errors };
    skuAttributeNames.forEach(attrName => {
      const errorKey = `attribute_${attrName}`;
      if (cleanedErrors[errorKey]) {
        delete cleanedErrors[errorKey];
      }
    });
    setErrors(cleanedErrors);

    // Get warehouse_id from localStorage as fallback
    const userWarehouseId = localStorage.getItem('warehouse_id');
    console.log('User warehouse_id for submission:', userWarehouseId);

    if (!formData.warehouse_id && userWarehouseId) {
      updateFormData('warehouse_id', userWarehouseId);
      console.log('Setting warehouse_id from localStorage:', userWarehouseId);
    }

    // Sync data from attributes to form state - FIXED: Handle brand object format
    if (!formData.brand_name && attributeValues.brand) {
      const brandValue = attributeValues.brand;
      if (typeof brandValue === 'object' && brandValue.name) {
        updateFormData('brand_name', brandValue.name);
        if (brandValue.brand_id) {
          updateFormData('brand_id', brandValue.brand_id.toString());
        }
      } else if (typeof brandValue === 'string') {
        updateFormData('brand_name', brandValue);
      }
    }

    // If formData.brand_name is an object, extract the name and id
    if (formData.brand_name && typeof formData.brand_name === 'object') {
      const brandObj = formData.brand_name;
      updateFormData('brand_name', brandObj.name || '');
      if (brandObj.brand_id) {
        updateFormData('brand_id', brandObj.brand_id.toString());
      }
    }

    // FIX: Ensure brand_id is always set from selectedBrand if available
    if (!formData.brand_id && selectedBrand && selectedBrand.brand_id) {
      updateFormData('brand_id', selectedBrand.brand_id.toString());
    }

    // Sync SKU, Price, Package data from attributes if they exist
    // But only if we're not using MultipleSKU component
    if (skuAttributes.length === 0) {
      if (attributeValues.SellerSku && !sku) {
        setSku(attributeValues.SellerSku);
      }
      if (attributeValues.price && !price) {
        setPrice(attributeValues.price);
      }
      if (attributeValues.package_weight && !packageData.weight) {
        setPackageData(prev => ({ ...prev, weight: attributeValues.package_weight }));
      }
      if (attributeValues.package_length && !packageData.length) {
        setPackageData(prev => ({ ...prev, length: attributeValues.package_length }));
      }
      if (attributeValues.package_width && !packageData.width) {
        setPackageData(prev => ({ ...prev, width: attributeValues.package_width }));
      }
      if (attributeValues.package_height && !packageData.height) {
        setPackageData(prev => ({ ...prev, height: attributeValues.package_height }));
      }
    }

    // Wait for state updates
    await new Promise(resolve => setTimeout(resolve, 100));

    const formErrors = validateForm();

    console.log('=== FORM VALIDATION DEBUG ===');
    console.log('Form Errors:', formErrors);
    console.log('Attribute Values:', attributeValues);
    console.log('=== END DEBUG ===');

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);

      const errorFields = Object.keys(formErrors);
      let errorMessage = 'Please fill all required fields correctly';

      if (errorFields.length > 0 && errorFields.length <= 3) {
        // Field name mapping for user-friendly display and duplicate prevention
        const fieldNameMap = {
          'company_id': 'Company',
          'warehouse_id': 'Warehouse',
          'category_id': 'Category',
          'brand_name': 'Brand name',
          'attribute_brand': 'Brand name', // Map attribute_brand to same display name
          'product_name': 'Product name',
          'description': 'Description',
          'barcode_number': 'Barcode',
          'storage_type': 'Storage type',
          'expire_date': 'Expiry date',
          'SellerSku': 'Seller SKU',
          'quantity': 'Quantity',
          'price': 'Price',
          'package_weight': 'Package weight',
          'package_length': 'Package length',
          'package_width': 'Package width',
          'package_height': 'Package height'
        };

        // Use Set to remove duplicates based on mapped field names
        const uniqueErrors = new Set();
        const firstErrors = errorFields.slice(0, 3).map(field => {
          // Extract clean field name
          let cleanFieldName = field
            .replace('attribute_', '')
            .replace('skus.0.', '')
            .replace(/\..*$/, ''); // Remove everything after dot

          // Use mapped name or generate from the field
          cleanFieldName = fieldNameMap[cleanFieldName] ||
            cleanFieldName.replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());

          const errorMessage = `${cleanFieldName} is required`;

          // Add to set to ensure uniqueness
          if (!uniqueErrors.has(errorMessage)) {
            uniqueErrors.add(errorMessage);
            return errorMessage;
          }
          return null;
        }).filter(Boolean); // Remove null values

        if (firstErrors.length > 0) {
          errorMessage = `Please fix the following: ${firstErrors.join('. ')}.`;
        }
      }

      showPopup('error', 'Validation Error', errorMessage);
      return;
    }

    setLoading(true);
    try {
      // Check if any SKU has images uploaded
      const hasSkuImages = skus.some(sku => sku.Images.Image.length > 0);

      if (!hasSkuImages) {
        showPopup('error', 'Error', 'Please upload at least one image for SKU variations');
        setLoading(false);
        return;
      }

      // Build the final payload - Use warehouse_id with priority: formData > localStorage
      let warehouseIdToUse = formData.warehouse_id;

      if (!warehouseIdToUse && userWarehouseId) {
        warehouseIdToUse = userWarehouseId;
        console.log('Using warehouse_id from localStorage:', warehouseIdToUse);
      }

      if (!warehouseIdToUse) {
        showPopup('error', 'Validation Error', 'Warehouse ID is required');
        setLoading(false);
        return;
      }

      // Build saleProp for SKU if size and color are available - UPDATED: Make values arrays
      const buildSaleProp = (sku) => {
        const saleProp = {};
        if (sku.saleProp && Object.keys(sku.saleProp).length > 0) {
          Object.keys(sku.saleProp).forEach(attrName => {
            const value = sku.saleProp[attrName];
            if (value && value.trim() !== '') {
              saleProp[attrName] = value;
            }
          });
        }
        return Object.keys(saleProp).length > 0 ? saleProp : undefined;
      };

      // Prepare SKUs data with saleProp
      const finalSkus = skus.map(sku => {
        // Filter out any blob URLs and use only proper image URLs
        const skuImages = sku.Images.Image.filter(url =>
          url.startsWith('http') && !url.startsWith('blob:')
        );

        return {
          SellerSku: sku.SellerSku,
          quantity: sku.quantity.toString(),
          price: sku.price.toString(),
          package_height: sku.package_height.toString(),
          package_length: sku.package_length.toString(),
          package_width: sku.package_width.toString(),
          package_weight: sku.package_weight.toString(),
          Images: {
            Image: skuImages // Use only SKU-specific images
          },
          ...(buildSaleProp(sku) && {
            saleProp: buildSaleProp(sku)
          }),
          package_content: sku.package_content,
          tax_class: sku.tax_class
        };
      });

      // Check if any SKU has valid images
      const hasValidSkuImages = finalSkus.some(sku => sku.Images.Image.length > 0);

      if (!hasValidSkuImages) {
        showPopup('error', 'Error', 'No valid images found in SKU variations. Please make sure images are properly uploaded.');
        setLoading(false);
        return;
      }

      // Create product payload
      // FIXED: Extract brand name and ID properly
      let brandName = '';
      let brandId = '';

      // Priority 1: Check selectedBrand from brand selector
      if (selectedBrand && selectedBrand.name && selectedBrand.name.trim() !== '') {
        brandName = selectedBrand.name;
        if (selectedBrand.brand_id) {
          brandId = selectedBrand.brand_id.toString();
        }
      }

      // Priority 2: Check formData.brand_name (from manual input)
      if (!brandName && formData.brand_name && formData.brand_name.trim() !== '') {
        // Handle both string and object formats
        if (typeof formData.brand_name === 'object' && formData.brand_name.name) {
          brandName = formData.brand_name.name;
          if (formData.brand_name.brand_id) {
            brandId = formData.brand_name.brand_id.toString();
          }
        } else {
          brandName = formData.brand_name;
        }
      }

      // Priority 3: Check attributeValues.brand (from attributes)
      if (!brandName && attributeValues.brand) {
        if (typeof attributeValues.brand === 'object' && attributeValues.brand.name) {
          brandName = attributeValues.brand.name;
          if (attributeValues.brand.brand_id) {
            brandId = attributeValues.brand.brand_id.toString();
          }
        } else if (typeof attributeValues.brand === 'string' && attributeValues.brand.trim() !== '') {
          brandName = attributeValues.brand;
        }
      }

      // Priority 4: Check formData.brand_id (direct brand ID)
      if (!brandId && formData.brand_id && formData.brand_id.trim() !== '') {
        brandId = formData.brand_id.toString();
      }

      // Final fallback: If no brand name found, set to "No Brand"
      if (!brandName || brandName.trim() === '') {
        brandName = 'No Brand';
      }

      console.log('Brand Debug Final:', {
        brandName,
        brandId,
        formDataBrandName: formData.brand_name,
        formDataBrandId: formData.brand_id,
        selectedBrand,
        attributeValuesBrand: attributeValues.brand
      });

      const productName = formData.product_name || attributeValues.name || '';
      const firstSkuImage = finalSkus[0]?.Images?.Image[0];
      const barcodeImageUrl = firstSkuImage ||
        `https://via.placeholder.com/400x400/ffffff/000000.png?text=BARCODE:${encodeURIComponent(formData.barcode_number)}`;

      // Build attributes object - EXCLUDE SKU, Price, Package fields as they go in SKU object
      const attributesObj = {
        name: productName,
        description: formData.description,
        ...(brandName && brandName !== 'No Brand' && { brand: brandName }),
        ...Object.keys(attributeValues).reduce((acc, key) => {
          const value = attributeValues[key];

          // Skip fields that go in SKU object
          const skuFields = ['SellerSku', 'sku', 'price', 'package_weight', 'package_length', 'package_width', 'package_height', 'quantity'];
          if (skuFields.includes(key)) return acc;

          // Skip ONLY color_family as it goes in saleProp
          const saleProperties = ['color_family'];
          if (saleProperties.includes(key)) return acc;

          // Skip brand as we already handled it above
          if (key === 'brand') return acc;

          if (value !== null && value !== undefined && value !== '' && value !== '0' && value !== 'undefined') {
            const attribute = attributes.find(attr => attr.name === key);
            if (attribute) {
              // Handle Hazmat specifically - convert array to string
              if (key.toLowerCase() === 'hazmat') {
                if (Array.isArray(value)) {
                  if (value.length === 0) {
                    acc[key] = "None";
                  } else {
                    const hazmatNames = value.map(val => {
                      if (attribute.options) {
                        const selectedOption = attribute.options.find(opt =>
                          opt.id.toString() === val.toString() || opt.name === val
                        );
                        return selectedOption ? selectedOption.name : val;
                      }
                      return val;
                    });
                    acc[key] = hazmatNames.length === 1 ? hazmatNames[0] : hazmatNames.join(', ');
                  }
                } else {
                  acc[key] = value.toString();
                }
              }
              // Handle multi-select attributes (arrays) - EXCEPT Hazmat
              else if ((attribute.input_type === 'multiSelect' || attribute.input_type === 'multiEnumInput') && Array.isArray(value)) {
                if (value.length > 0) {
                  const multiSelectNames = value.map(val => {
                    if (attribute.options) {
                      const selectedOption = attribute.options.find(opt =>
                        opt.id.toString() === val.toString() || opt.name === val
                      );
                      return selectedOption ? selectedOption.name : val;
                    }
                    return val;
                  });
                  acc[key] = multiSelectNames.join(',');
                } else {
                  acc[key] = '';
                }
              }
              // Handle single select values - send display names instead of IDs
              else if ((attribute.input_type === 'singleSelect' || attribute.input_type === 'enumInput') && attribute.options) {
                const selectedOption = attribute.options.find(opt =>
                  opt.id.toString() === value.toString() || opt.name === value
                );
                if (selectedOption) {
                  acc[key] = selectedOption.name;
                } else {
                  acc[key] = value;
                }
              }
              // Handle other input types
              else if (attribute.input_type === 'textInput') {
                acc[key] = value.toString();
              } else if (attribute.input_type === 'numericInput') {
                acc[key] = parseFloat(value) || 0;
              } else {
                acc[key] = value;
              }
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {})
      };

      // Add brand_id to attributes if available - ONLY if we have a valid brand ID
      if (brandId && brandId.trim() !== '' && brandId !== 'undefined') {
        attributesObj.brand_id = brandId;
      }

      // If no brand was set in attributes but we have "No Brand", include it
      if (!attributesObj.brand && brandName === 'No Brand') {
        attributesObj.brand = 'No Brand';
      }

      // Get all unique images from all SKUs for main product images
      const allSkuImages = finalSkus.flatMap(sku => sku.Images.Image);
      const uniqueImages = [...new Set(allSkuImages)]; // Remove duplicates
      const mainProductImages = uniqueImages.slice(0, 5); // Max 5 images

      // FIX: ALWAYS include storage_type and expire_date with default values
      const storageTypeValue = formData.productType === 'perishable'
        ? (formData.storage_type || 'dry')
        : 'dry';

      const expireDateValue = formData.productType === 'perishable'
        ? (formData.expire_date || '2099-12-31')
        : '2099-12-31';

      console.log('Storage Type Value:', storageTypeValue);
      console.log('Expire Date Value:', expireDateValue);

      // Build the final payload - ALWAYS include storage_type and expire_date
      const payload = {
        company_id: parseInt(formData.company_id),
        storage_type: storageTypeValue,
        warehouse_id: parseInt(warehouseIdToUse),
        expire_date: expireDateValue,
        status: 1,
        barcode_number: formData.barcode_number.toString(),
        barcode_image: barcodeImageUrl,
        quantity: 1,
        e_commerce_platform: 'lazada',
        is_new_transaction: transactionId ? 'false' : 'true',
        ...(transactionId && transactionId !== 'new' && { transaction_id: parseInt(transactionId) }),

        Request: {
          Product: {
            PrimaryCategory: formData.category_id.toString(),
            Images: {
              Image: mainProductImages
            },
            Attributes: attributesObj,
            Skus: {
              Sku: finalSkus
            }
          }
        }
      };

      console.log('Final Payload:', JSON.stringify(payload, null, 2));

      const response = await createProduct(payload);
      if (response.status === 200) {
        showPopup('success', 'Success', 'Product created successfully');
        setTimeout(() => {
          // navigate(`/products/details/${response.data?.transaction_id}`);
          navigate('/products');
        }, 1000);
      } else {
        showPopup('error', 'Error', response.message || 'Failed to create product');
      }
    } catch (error) {
      console.error('API Error:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        console.error('Error Response Data:', errorData);
        let errorMessage = errorData.message || 'Failed to create product';
        if (errorData.data && Array.isArray(errorData.data)) {
          const lazadaErrors = errorData.data;
          lazadaErrors.forEach(err => {
            errorMessage += `\nField: ${err.field}, Message: ${err.message}`;
          });
        }
        showPopup('error', 'Create Product Failed', errorMessage);
      } else {
        showPopup('error', 'Error', error.message || 'Failed to create product');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'barcode_number' && isBarcodeFromScan) {
      return;
    }

    updateFormData(name, value);

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (['height', 'length', 'width', 'weight'].includes(name)) {
      setPackageData(prev => ({ ...prev, [name]: value }));
      if (errors.package) {
        setErrors(prev => ({ ...prev, package: '' }));
      }
    }

    if (name === 'productType' && value === 'non-perishable') {
      updateFormData('storage_type', '');
      updateFormData('expire_date', '');
      if (errors.storage_type || errors.expire_date) {
        setErrors(prev => ({ ...prev, storage_type: '', expire_date: '' }));
      }
    }
  };

  return (
    <div className={`${styles.product_add}`}>
      <PopUpMessage />
      <div className={`${styles.product_heading}`}>
        <h2>Create Product</h2>
      </div>

      <div className={`${styles.product_form}`}>
        <div className={`${styles.product_details}`}>
          <div className={`${styles.title}`}>
            <h4>Product details</h4>
            <hr />
          </div>
          <div className={`${styles.form}`}>
            <ProductBasicInfo
              formData={formData}
              errors={errors}
              companies={companies}
              warehouses={warehouses}
              updateFormData={updateFormData}
              clearError={clearError}
            />

            <CategorySelector
              formData={formData}
              errors={errors}
              onCategorySelect={handleCategorySelect}
              categorySearchTerm={categorySearchTerm}
              setCategorySearchTerm={setCategorySearchTerm}
              selectedCategoryName={selectedCategoryName}
              setSelectedCategoryName={setSelectedCategoryName}
              clearError={clearError}
              showPopup={showPopup}
            />

            {loadingAttributes && (
              <div className={`${styles.loading_container}`}>
                <p>Loading attributes...</p>
              </div>
            )}

            {/* Mandatory Attributes */}
            <AttributeFields
              // attributes={attributes}
              attributes={filteredMandatoryAttributes}
              // attributes={mandatoryAttributes}
              attributeValues={attributeValues}
              errors={errors}
              onAttributeChange={handleAttributeChange}
              clearAttributeError={clearAttributeError}
              loadingAttributes={loadingAttributes}
            />

            {/* Optional Attributes */}
            {/* <OptionalAttributes
              optionalAttributes={optionalAttributes}
              attributeValues={attributeValues}
              errors={errors}
              onAttributeChange={handleAttributeChange}
              clearAttributeError={clearAttributeError}
              loadingAttributes={loadingAttributes}
            /> */}

            {/* <BrandSelector
              formData={formData}
              errors={errors}
              brandSuggestions={brandSuggestions}
              brandsLoading={brandsLoading}
              onBrandSearch={handleBrandSearch}
              onBrandSelect={handleBrandSelect}
              brandSearchTerm={brandSearchTerm}
              setBrandSearchTerm={setBrandSearchTerm}
              showBrandDropdown={showBrandDropdown}
              setShowBrandDropdown={setShowBrandDropdown}
              selectedBrand={selectedBrand}
              setSelectedBrand={setSelectedBrand}
              updateFormData={updateFormData}
              clearError={clearError}
              fieldsToHide={fieldsToHide}
            /> */}

            {/* Multiple SKU Component - Show only if there are SKU attributes */}
            <div className={`${styles.sku_section}`}>
              {skuAttributes.length > 0 ? (
                <MultipleSKU
                  skus={skus}
                  setSkus={setSkus}
                  skuAttributes={skuAttributes}
                  errors={errors}
                  previewImages={previewImages}
                  showPopup={showPopup}
                />
              ) : (
                /* Your existing single SKU fields */
                <div className={`${styles.single_sku_section}`}>
                  {/* Single SKU fields here */}
                  {!shouldHideField('sku') && (
                    <div className={`${styles.label_input} ${errors.sku ? styles.error : ''}`}>
                      <label>Seller SKU<span>*</span></label>
                      <div className={`${styles.input_container}`}>
                        <div className={`${styles.inputWithButton}`}>
                          <input
                            type="text"
                            name="sku"
                            value={sku}
                            readOnly
                            placeholder="Click Generate to create SKU"
                            onFocus={() => clearError('sku')}
                          />
                          <button type="button" onClick={generateSKU}>Generate</button>
                        </div>
                        {errors.sku && <span className={`${styles.errorMessage}`}>{errors.sku}</span>}
                      </div>
                    </div>
                  )}
                  {/* ... other single SKU fields ... */}
                </div>
              )}
            </div>

            {/* {!attributes.find(attr => attr.name === 'price') && (
              <div className={`${styles.label_input} ${errors.price ? styles.error : ''}`}>
                <label>Price<span>*</span></label>
                <div className={`${styles.input_container}`}>
                  <input
                    type="number"
                    name="price"
                    value={price}
                    onChange={(e) => {
                      setPrice(e.target.value);
                      if (errors.price) setErrors(prev => ({ ...prev, price: '' }));
                    }}
                    onFocus={() => setErrors(prev => ({ ...prev, price: '' }))}
                    placeholder="Enter Price"
                  />
                  {errors.price && <span className={`${styles.errorMessage}`}>{errors.price}</span>}
                </div>
              </div>
            )} */}

            {/* {!attributes.find(attr => attr.name === 'package_weight') &&
              !attributes.find(attr => attr.name === 'package_length') &&
              !attributes.find(attr => attr.name === 'package_width') &&
              !attributes.find(attr => attr.name === 'package_height') && (
                <div className={`${styles.label_input} ${errors.package ? styles.error : ''}`}>
                  <label>Package Dimensions (cm/kg)<span>*</span></label>
                  <div className={`${styles.package_grid}`}>
                    <div className={`${styles.package_field}`}>
                      <input
                        type="number"
                        name="height"
                        value={packageData.height}
                        onChange={(e) => {
                          setPackageData(prev => ({ ...prev, height: e.target.value }));
                          if (errors.package) setErrors(prev => ({ ...prev, package: '' }));
                        }}
                        placeholder="Height (cm)"
                      />
                    </div>
                    <div className={`${styles.package_field}`}>
                      <input
                        type="number"
                        name="length"
                        value={packageData.length}
                        onChange={(e) => {
                          setPackageData(prev => ({ ...prev, length: e.target.value }));
                          if (errors.package) setErrors(prev => ({ ...prev, package: '' }));
                        }}
                        placeholder="Length (cm)"
                      />
                    </div>
                    <div className={`${styles.package_field}`}>
                      <input
                        type="number"
                        name="width"
                        value={packageData.width}
                        onChange={(e) => {
                          setPackageData(prev => ({ ...prev, width: e.target.value }));
                          if (errors.package) setErrors(prev => ({ ...prev, package: '' }));
                        }}
                        placeholder="Width (cm)"
                      />
                    </div>
                    <div className={`${styles.package_field}`}>
                      <input
                        type="number"
                        name="weight"
                        value={packageData.weight}
                        onChange={(e) => {
                          setPackageData(prev => ({ ...prev, weight: e.target.value }));
                          if (errors.package) setErrors(prev => ({ ...prev, package: '' }));
                        }}
                        placeholder="Weight (kg)"
                      />
                    </div>
                  </div>
                  {errors.package && <span className={`${styles.errorMessage}`}>{errors.package}</span>}
                </div>
              )} */}

            {/* <ImageUploader
              formData={formData}
              errors={errors}
              previewImages={previewImages}
              onImageUpload={handleImageUpload}
              onImageRemove={removeImage}
              clearError={clearError}
            /> */}

            {/* Description */}
            <div className={`${styles.label_input} ${errors.description ? styles.error : ''}`}>
              <label>Description<span>*</span></label>
              <div className={`${styles.input_container}`}>
                <CustomCKEditor
                  value={formData.description}
                  onChange={(value) => {
                    updateFormData('description', value);
                    clearError('description');
                  }}
                  onFocus={() => clearError('description')}
                  error={errors.description}
                  placeholder="Enter Product Description"
                  height="200px"
                />
                {errors.description && <span className={`${styles.errorMessage}`}>{errors.description}</span>}
              </div>
            </div>

            <PerishableFields
              formData={formData}
              errors={errors}
              updateFormData={updateFormData}
              clearError={clearError}
            />

            <BarcodeField
              formData={formData}
              errors={errors}
              isBarcodeFromScan={isBarcodeFromScan}
              updateFormData={updateFormData}
              onEnableEdit={enableManualBarcodeEdit}
              clearError={clearError}
            />
          </div>

          <FormButtons
            loading={loading}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            transactionId={transactionId}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductAdd;